import { useMemo } from 'react'

import { ProviderInfo } from '../../../models'
import { FAVORITED_PROVIDERS_PAGE_STATES } from '../../common/constants/mixpanelConstants'
import {
  FavoritedProviderProfilesFetchStatus,
  FavoritedProvidersValidationAction,
} from '../../favoritedProviders/types'

/**
 * Hook that determines the current page version/state for the favorited providers page
 * based on validation results, provider availability, and loading states.
 *
 * Currently used for mixpanel tracking, though could be easily used for other purposes in the future
 */
export const useGetFavoritedProvidersPageVersion = ({
  validationAction,
  availableProviders,
  unavailableProviders,
  fetchingFavoritedProvidersProfilesStatus,
  validationComplete,
  favoritedProvidersByIds,
}: {
  validationAction: FavoritedProvidersValidationAction | null
  availableProviders: ProviderInfo[]
  unavailableProviders: ProviderInfo[]
  fetchingFavoritedProvidersProfilesStatus: FavoritedProviderProfilesFetchStatus
  validationComplete: boolean | null
  favoritedProvidersByIds: string[] | null
}) => {
  return useMemo(() => {
    // Show loading state while fetching provider profiles or validation is in progress
    if (
      fetchingFavoritedProvidersProfilesStatus === FavoritedProviderProfilesFetchStatus.PENDING ||
      validationComplete === false ||
      validationComplete === null ||
      !Array.isArray(favoritedProvidersByIds) ||
      favoritedProvidersByIds.length !== availableProviders.length + unavailableProviders.length
    ) {
      return undefined
    }

    // Handle validation states first (highest priority)
    if (validationAction === FavoritedProvidersValidationAction.REFINED_FAVORITES) {
      return FAVORITED_PROVIDERS_PAGE_STATES.REFINED_FAVORITES
    }

    if (validationAction === FavoritedProvidersValidationAction.ALL_PROVIDERS_CLEARED) {
      return FAVORITED_PROVIDERS_PAGE_STATES.ALL_PROVIDERS_CLEARED
    }

    if (!availableProviders.length && !unavailableProviders.length) {
      return FAVORITED_PROVIDERS_PAGE_STATES.EXPIRED_OR_NONE_FAVORITED
    }
    if (availableProviders.length && !unavailableProviders.length) {
      return FAVORITED_PROVIDERS_PAGE_STATES.ALL_AVAILABLE
    }
    if (!availableProviders.length && unavailableProviders.length) {
      return FAVORITED_PROVIDERS_PAGE_STATES.NONE_AVAILABLE
    }
    if (availableProviders.length && unavailableProviders.length) {
      return FAVORITED_PROVIDERS_PAGE_STATES.SOME_AVAILABLE
    }
    return 'error' // Should never happen, but we'll emit 'error' for tracking
  }, [
    validationAction,
    availableProviders.length,
    unavailableProviders.length,
    fetchingFavoritedProvidersProfilesStatus,
    validationComplete,
    favoritedProvidersByIds,
  ])
}
